import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { serverApi } from '../../api';
import logo from '../../assets/logo-PIT-1-768x288-fondo.png';
import { useHideMenu } from '../../hooks/useHideMenu';
import { NuevaEmpresaModal } from './NuevaEmpresaModal';
import { NuevoVendedorModal } from './NuevoVendedorModal';
import { EditarEmpresaModal } from './EditarEmpresaModal';
import { RolesPantallasSuperAdmin } from './RolesPantallasSuperAdmin';

const MySwal = withReactContent(Swal);

export const SuperAdminPanel = () => {
    useHideMenu(true);
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState('empresas'); // 'empresas' | 'vendedores' | 'precios'
    const [loading, setLoading] = useState(false);

    // Listas de datos
    const [empresas, setEmpresas] = useState([]);
    const [vendedores, setVendedores] = useState([]);
    const [precios, setPrecios] = useState({ precioBaseSede: 500000, precioPorSedeAdicional: 100000, moneda: 'COP' });

    // Modales
    const [showEmpresaModal, setShowEmpresaModal] = useState(false);
    const [showEditarEmpresaModal, setShowEditarEmpresaModal] = useState(false);
    const [empresaToEdit, setEmpresaToEdit] = useState(null);
    const [showVendedorModal, setShowVendedorModal] = useState(false);
    const [vendedorToEdit, setVendedorToEdit] = useState(null);

    const handleEditarEmpresa = (empresa) => {
        setEmpresaToEdit(empresa);
        setShowEditarEmpresaModal(true);
    };

    const userRaw = localStorage.getItem('saUser');
    const saUser = userRaw ? JSON.parse(userRaw) : { nombreCompleto: 'Super Admin' };

    const cargarDatos = useCallback(async () => {
        try {
            setLoading(true);
            const [respEmp, respVend, respPrec] = await Promise.all([
                serverApi.get('/superadmin/entidades'),
                serverApi.get('/superadmin/vendedores'),
                serverApi.get('/superadmin/precio'),
            ]);

            if (respEmp.data.ok) setEmpresas(respEmp.data.entidades || []);
            if (respVend.data.ok) setVendedores(respVend.data.vendedores || []);
            if (respPrec.data.ok) setPrecios(respPrec.data.precios || {});
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        cargarDatos();
    }, [cargarDatos]);

    const handleLogout = () => {
        localStorage.removeItem('saToken');
        localStorage.removeItem('saUser');
        navigate('/superadmin');
    };

    const handleSavePrecios = async (e) => {
        e.preventDefault();
        try {
            const resp = await serverApi.put('/superadmin/precio', precios);
            if (resp.data.ok) {
                MySwal.fire({
                    title: '¡Precios Actualizados!',
                    text: resp.data.msg,
                    icon: 'success',
                    timer: 1500,
                    showConfirmButton: false,
                });
            }
        } catch (error) {
            MySwal.fire('Error', 'No se pudieron actualizar los precios', 'error');
        }
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: '#f4f7f6',
            fontFamily: "'Inter', Arial, sans-serif",
            paddingBottom: 40,
        }}>
            {/* Topbar Brand */}
            <div style={{
                background: 'linear-gradient(135deg, #0d7367 0%, #0c695e 100%)',
                color: 'white',
                padding: '16px 32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxShadow: '0 4px 20px rgba(13,115,103,0.25)',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <img src={logo} alt="logo" style={{ height: 58, marginBottom: 12, borderRadius: 10, }} />

                    <div>
                        <h3 style={{ margin: 0, fontWeight: 900, fontSize: 18 }}>Panel SuperAdmin</h3>
                        <span style={{ fontSize: 12, opacity: 0.85 }}>Plataforma Marca Blanca Multi-Tenant</span>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                        background: 'rgba(255,255,255,0.15)',
                        padding: '6px 14px',
                        borderRadius: 20,
                        fontSize: 13,
                        fontWeight: 700,
                    }}>
                        👤 {saUser.nombreCompleto || 'Super Admin'}
                    </div>
                    <button
                        onClick={handleLogout}
                        style={{
                            background: '#ef4444',
                            border: 'none',
                            color: 'white',
                            padding: '8px 16px',
                            borderRadius: 10,
                            fontWeight: 700,
                            cursor: 'pointer',
                            fontSize: 13,
                        }}
                    >
                        Cerrar Sesión ✕
                    </button>
                </div>
            </div>

            {/* Container */}
            <div style={{ maxWidth: 1200, margin: '28px auto 0', padding: '0 20px' }}>
                {/* Tabs */}
                <div style={{
                    display: 'flex',
                    gap: 10,
                    marginBottom: 24,
                    borderBottom: '2px solid #e2e8f0',
                    paddingBottom: 12,
                }}>
                    <button
                        onClick={() => setActiveTab('empresas')}
                        style={{
                            padding: '10px 20px',
                            borderRadius: 12,
                            border: 'none',
                            background: activeTab === 'empresas' ? '#0d7367' : '#ffffff',
                            color: activeTab === 'empresas' ? 'white' : '#475569',
                            fontWeight: 800,
                            fontSize: 14,
                            cursor: 'pointer',
                            boxShadow: activeTab === 'empresas' ? '0 4px 12px rgba(13,115,103,0.3)' : '0 2px 6px rgba(0,0,0,0.04)',
                        }}
                    >
                        🏢 Empresas ({empresas.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('vendedores')}
                        style={{
                            padding: '10px 20px',
                            borderRadius: 12,
                            border: 'none',
                            background: activeTab === 'vendedores' ? '#0d7367' : '#ffffff',
                            color: activeTab === 'vendedores' ? 'white' : '#475569',
                            fontWeight: 800,
                            fontSize: 14,
                            cursor: 'pointer',
                            boxShadow: activeTab === 'vendedores' ? '0 4px 12px rgba(13,115,103,0.3)' : '0 2px 6px rgba(0,0,0,0.04)',
                        }}
                    >
                        💼 Vendedores ({vendedores.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('roles')}
                        style={{
                            padding: '10px 20px',
                            borderRadius: 12,
                            border: 'none',
                            background: activeTab === 'roles' ? '#0d7367' : '#ffffff',
                            color: activeTab === 'roles' ? 'white' : '#475569',
                            fontWeight: 800,
                            fontSize: 14,
                            cursor: 'pointer',
                            boxShadow: activeTab === 'roles' ? '0 4px 12px rgba(13,115,103,0.3)' : '0 2px 6px rgba(0,0,0,0.04)',
                        }}
                    >
                        🛡️ Roles & Pantallas
                    </button>
                    <button
                        onClick={() => setActiveTab('precios')}
                        style={{
                            padding: '10px 20px',
                            borderRadius: 12,
                            border: 'none',
                            background: activeTab === 'precios' ? '#0d7367' : '#ffffff',
                            color: activeTab === 'precios' ? 'white' : '#475569',
                            fontWeight: 800,
                            fontSize: 14,
                            cursor: 'pointer',
                            boxShadow: activeTab === 'precios' ? '0 4px 12px rgba(13,115,103,0.3)' : '0 2px 6px rgba(0,0,0,0.04)',
                        }}
                    >
                        💰 Precios Plataforma
                    </button>
                </div>

                {/* TAB 1: EMPRESAS */}
                {activeTab === 'empresas' && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <div>
                                <h3 style={{ margin: 0, fontWeight: 800, color: '#0f172a' }}>Directorio de Empresas Clientes</h3>
                                <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>Entidades registradas y sedes autorizadas</p>
                            </div>
                            <button
                                onClick={() => setShowEmpresaModal(true)}
                                style={{
                                    padding: '12px 22px',
                                    borderRadius: 12,
                                    border: 'none',
                                    background: 'linear-gradient(135deg, #0d7367 0%, #14b8a6 100%)',
                                    color: 'white',
                                    fontWeight: 800,
                                    fontSize: 14,
                                    cursor: 'pointer',
                                    boxShadow: '0 4px 14px rgba(13,115,103,0.35)',
                                }}
                            >
                                ➕ Crear Nueva Empresa
                            </button>
                        </div>

                        {/* Tabla Empresas */}
                        <div style={{ background: 'white', borderRadius: 16, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', color: '#475569', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                        <th style={{ padding: '14px 18px' }}>Código</th>
                                        <th style={{ padding: '14px 18px' }}>Empresa</th>
                                        <th style={{ padding: '14px 18px' }}>Vendedor</th>
                                        <th style={{ padding: '14px 18px', textAlign: 'center' }}>Sedes Max</th>
                                        <th style={{ padding: '14px 18px', textAlign: 'center' }}>Usuarios</th>
                                        <th style={{ padding: '14px 18px' }}>Fecha Creación</th>
                                        <th style={{ padding: '14px 18px', textAlign: 'center' }}>Estado</th>
                                        <th style={{ padding: '14px 18px', textAlign: 'center' }}>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {empresas.length === 0 ? (
                                        <tr>
                                            <td colSpan="8" style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>
                                                No hay empresas creadas todavía.
                                            </td>
                                        </tr>
                                    ) : (
                                        empresas.map(e => (
                                            <tr key={e.codigoEntidad} style={{ borderBottom: '1px solid #f1f5f9', fontSize: 14 }}>
                                                <td style={{ padding: '14px 18px', fontWeight: 800, color: '#0d7367' }}>
                                                    {e.codigoEntidad}
                                                </td>
                                                <td style={{ padding: '14px 18px', fontWeight: 700, color: '#0f172a' }}>
                                                    {e.nombreEntidad}
                                                    {e.ciudad && <span style={{ display: 'block', fontSize: 12, color: '#64748b', fontWeight: 400 }}>{e.ciudad}</span>}
                                                </td>
                                                <td style={{ padding: '14px 18px', color: '#475569' }}>
                                                    {e.nombreVendedor ? <b>{e.nombreVendedor}</b> : <span style={{ color: '#94a3b8' }}>Directo / SuperAdmin</span>}
                                                </td>
                                                <td style={{ padding: '14px 18px', textAlign: 'center' }}>
                                                    <span style={{ background: '#ecfdf5', color: '#047857', fontWeight: 800, padding: '4px 10px', borderRadius: 12, fontSize: 13 }}>
                                                        {e.sedesRegistradas || 1} / {e.maxSedes} sedes
                                                    </span>
                                                </td>
                                                <td style={{ padding: '14px 18px', textAlign: 'center', fontWeight: 700, color: '#475569' }}>
                                                    {e.totalUsuarios || 0}
                                                </td>
                                                <td style={{ padding: '14px 18px', color: '#64748b', fontSize: 13 }}>
                                                    {e.fechaCreacion ? String(e.fechaCreacion).substring(0, 10) : '—'}
                                                </td>
                                                <td style={{ padding: '14px 18px', textAlign: 'center' }}>
                                                    <span style={{
                                                        background: e.indActivo ? '#dcfce7' : '#fee2e2',
                                                        color: e.indActivo ? '#15803d' : '#b91c1c',
                                                        fontWeight: 800,
                                                        fontSize: 12,
                                                        padding: '4px 10px',
                                                        borderRadius: 20,
                                                    }}>
                                                        {e.indActivo ? 'ACTIVA' : 'INACTIVA'}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '14px 18px', textAlign: 'center' }}>
                                                    <button
                                                        onClick={() => handleEditarEmpresa(e)}
                                                        style={{
                                                            background: '#eff6ff',
                                                            border: '1px solid #bfdbfe',
                                                            color: '#1e40af',
                                                            borderRadius: 8,
                                                            padding: '6px 14px',
                                                            fontSize: 12,
                                                            fontWeight: 700,
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        ✏️ Editar
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* TAB 2: VENDEDORES */}
                {activeTab === 'vendedores' && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <div>
                                <h3 style={{ margin: 0, fontWeight: 800, color: '#0f172a' }}>Gestión de Vendedores</h3>
                                <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>Equipo comercial autorizado para vender empresas</p>
                            </div>
                            <button
                                onClick={() => { setVendedorToEdit(null); setShowVendedorModal(true); }}
                                style={{
                                    padding: '12px 22px',
                                    borderRadius: 12,
                                    border: 'none',
                                    background: 'linear-gradient(135deg, #0d7367 0%, #14b8a6 100%)',
                                    color: 'white',
                                    fontWeight: 800,
                                    fontSize: 14,
                                    cursor: 'pointer',
                                    boxShadow: '0 4px 14px rgba(13,115,103,0.35)',
                                }}
                            >
                                ➕ Crear Nuevo Vendedor
                            </button>
                        </div>

                        <div style={{ background: 'white', borderRadius: 16, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', color: '#475569', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                        <th style={{ padding: '14px 18px' }}>Código</th>
                                        <th style={{ padding: '14px 18px' }}>Nombre Vendedor</th>
                                        <th style={{ padding: '14px 18px' }}>Email</th>
                                        <th style={{ padding: '14px 18px', textAlign: 'center' }}>Empresas Vendidas</th>
                                        <th style={{ padding: '14px 18px', textAlign: 'center' }}>Estado</th>
                                        <th style={{ padding: '14px 18px', textAlign: 'center' }}>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {vendedores.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>
                                                No hay vendedores registrados aún.
                                            </td>
                                        </tr>
                                    ) : (
                                        vendedores.map(v => (
                                            <tr key={v.codigoVendedor} style={{ borderBottom: '1px solid #f1f5f9', fontSize: 14 }}>
                                                <td style={{ padding: '14px 18px', fontWeight: 800, color: '#0d7367' }}>
                                                    {v.codigoVendedor}
                                                </td>
                                                <td style={{ padding: '14px 18px', fontWeight: 700, color: '#0f172a' }}>
                                                    {v.nombreVendedor}
                                                </td>
                                                <td style={{ padding: '14px 18px', color: '#475569' }}>
                                                    {v.emailVendedor}
                                                </td>
                                                <td style={{ padding: '14px 18px', textAlign: 'center' }}>
                                                    <span style={{ background: '#e0f2fe', color: '#0369a1', fontWeight: 800, padding: '4px 12px', borderRadius: 12 }}>
                                                        {v.totalEmpresas || 0} empresas
                                                    </span>
                                                </td>
                                                <td style={{ padding: '14px 18px', textAlign: 'center' }}>
                                                    <span style={{
                                                        background: v.indActivo ? '#dcfce7' : '#fee2e2',
                                                        color: v.indActivo ? '#15803d' : '#b91c1c',
                                                        fontWeight: 800, fontSize: 12, padding: '4px 10px', borderRadius: 20
                                                    }}>
                                                        {v.indActivo ? 'ACTIVO' : 'INACTIVO'}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '14px 18px', textAlign: 'center' }}>
                                                    <button
                                                        onClick={() => { setVendedorToEdit(v); setShowVendedorModal(true); }}
                                                        style={{
                                                            background: '#f1f5f9', border: '1px solid #cbd5e1', color: '#0d7367',
                                                            borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer'
                                                        }}
                                                    >
                                                        ✏️ Editar
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* TAB 3: ROLES Y PANTALLAS */}
                {activeTab === 'roles' && (
                    <RolesPantallasSuperAdmin />
                )}

                {/* TAB 4: PRECIOS */}
                {activeTab === 'precios' && (
                    <div style={{ maxWidth: 540 }}>
                        <h3 style={{ margin: '0 0 6px', fontWeight: 800, color: '#0f172a' }}>Configuración de Precios Plataforma</h3>
                        <p style={{ margin: '0 0 20px', color: '#64748b', fontSize: 13 }}>Valores asignados por el SuperAdmin para cobro de sedes</p>

                        <div style={{ background: 'white', borderRadius: 20, padding: 28, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
                            <form onSubmit={handleSavePrecios}>
                                <div style={{ marginBottom: 18 }}>
                                    <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                        Precio Paquete Base (1ª Sede)
                                    </label>
                                    <input
                                        type="number"
                                        value={precios.precioBaseSede}
                                        onChange={e => setPrecios(p => ({ ...p, precioBaseSede: e.target.value }))}
                                        required
                                        style={{
                                            width: '100%', padding: '12px 14px', borderRadius: 12,
                                            border: '1.5px solid #cbd5e1', fontSize: 18, fontWeight: 800,
                                            color: '#0d7367', marginTop: 6, boxSizing: 'border-box',
                                        }}
                                    />
                                    <span style={{ fontSize: 12, color: '#64748b', marginTop: 4, display: 'block' }}>
                                        Valor actual: <b>${Number(precios.precioBaseSede || 0).toLocaleString('es-CO')} COP</b>
                                    </span>
                                </div>

                                <div style={{ marginBottom: 24 }}>
                                    <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                        Precio por Sede Adicional
                                    </label>
                                    <input
                                        type="number"
                                        value={precios.precioPorSedeAdicional}
                                        onChange={e => setPrecios(p => ({ ...p, precioPorSedeAdicional: e.target.value }))}
                                        required
                                        style={{
                                            width: '100%', padding: '12px 14px', borderRadius: 12,
                                            border: '1.5px solid #cbd5e1', fontSize: 18, fontWeight: 800,
                                            color: '#0d7367', marginTop: 6, boxSizing: 'border-box',
                                        }}
                                    />
                                    <span style={{ fontSize: 12, color: '#64748b', marginTop: 4, display: 'block' }}>
                                        Valor actual: <b>${Number(precios.precioPorSedeAdicional || 0).toLocaleString('es-CO')} COP</b> por sede extra
                                    </span>
                                </div>

                                <button
                                    type="submit"
                                    style={{
                                        width: '100%', padding: 14, borderRadius: 12,
                                        border: 'none', background: 'linear-gradient(135deg, #0d7367 0%, #14b8a6 100%)',
                                        color: 'white', fontWeight: 800, fontSize: 15, cursor: 'pointer',
                                        boxShadow: '0 4px 14px rgba(13,115,103,0.3)',
                                    }}
                                >
                                    💾 GUARDAR CONFIGURACIÓN DE PRECIOS
                                </button>
                            </form>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal Nueva Empresa */}
            <NuevaEmpresaModal
                show={showEmpresaModal}
                onClose={() => setShowEmpresaModal(false)}
                onSuccess={cargarDatos}
                vendedores={vendedores}
                preciosConfig={precios}
            />

            {/* Modal Editar Empresa */}
            <EditarEmpresaModal
                show={showEditarEmpresaModal}
                onClose={() => { setShowEditarEmpresaModal(false); setEmpresaToEdit(null); }}
                onSuccess={cargarDatos}
                empresa={empresaToEdit}
            />

            {/* Modal Nuevo Vendedor */}
            <NuevoVendedorModal
                show={showVendedorModal}
                onClose={() => setShowVendedorModal(false)}
                onSuccess={cargarDatos}
                vendedorToEdit={vendedorToEdit}
            />
        </div>
    );
};
